import React, { useState } from 'react';
import { Footer } from 'components/Footer';
import { observer } from 'mobx-react-lite';
import { SignupCard } from 'screens/Signup/components/SignupCard';
import { SignupViewModel } from 'screens/Signup/SignupViewModel';
import styled, { ThemeProvider } from 'styled-components';
import { ScreenContainer } from 'styleguide/ScreenContainer';
import { H3, P2, P4 } from 'styleguide/Texts';
import { themeState } from 'styleguide/theme';
import DownloadGooglePlay from 'assets/download-googleplay.svg';
import { colors } from 'styleguide/colors';
import { ProfileImage } from 'components/ProfileImage';
import { PrimaryButton, SecondaryButton } from 'styleguide/Buttons';
import { View } from 'styleguide/View';
import { AcceptInviteViewModel } from './AcceptInviteViewModel';
import { stores } from 'state/stores';
import { LoginCard } from 'screens/Login/components/LoginCard';
import { LoginViewModel } from 'screens/Login/LoginViewModel';
import { ForgotPasswordCard } from 'screens/Login/components/ForgotPasswordCard';
import toast from 'react-hot-toast';
import { Card } from 'styleguide/Card';
import { useGoogleLogin } from '@react-oauth/google';
import { t } from 'translations/t';

const Spacer = styled.div`
  margin-top: 92px;
  @media (max-width: 1024px) {
    margin-top: 16px;
  }
`;

export interface AcceptScreenProps {
  // Invites can be accepted based on short ID (emailed link), or the coach username directly
  inviteShortId?: string;
  coachName?: string;
  coachUsername: string;
  coachProfilePic?: string;
  inviteeUsername?: string;
}

export const AcceptInviteScreen = observer(
  ({
    inviteShortId,
    coachName,
    coachUsername,
    coachProfilePic,
    inviteeUsername,
  }: AcceptScreenProps) => {
    const [acceptInviteVM] = useState(
      () =>
        new AcceptInviteViewModel({
          coachUsername,
          coachFullname: coachName,
          inviteeUsername: inviteeUsername ?? undefined,
          inviteShortId,
        }),
    );

    const [signupVM] = useState(
      () =>
        new SignupViewModel({
          coachInviteShortId: acceptInviteVM.inviteShortId,
          afterSignup: async isNewUser => {
            await acceptInviteVM.onSignUpOrSignInComplete({ isNewUser });
          },
        }),
    );

    const [loginVM] = useState(
      () =>
        new LoginViewModel({
          afterLogin: async () => {
            await acceptInviteVM.onSignUpOrSignInComplete({ isNewUser: false });
          },
        }),
    );

    const { isAuthenticated } = stores.auth;
    const isUserAlreadyLoggedIn = isAuthenticated && !acceptInviteVM.isProcessingLoginOrSignup;

    const onSignup = async () => {
      if (!signupVM.isSignupButtonEnabled) return;

      acceptInviteVM.isProcessingLoginOrSignup = true;
      await signupVM.onSignupCoachClientPress();
    };

    const onAppleSignupPress = async () => {
      acceptInviteVM.isProcessingLoginOrSignup = true;
      await signupVM.onSignupWithAppleClick();
    };

    const onLogin = async () => {
      if (!loginVM.isLoginButtonEnabled) return;

      acceptInviteVM.isProcessingLoginOrSignup = true;
      await loginVM.onLoginPress();
    };

    const onApplePress = async () => {
      acceptInviteVM.isProcessingLoginOrSignup = true;
      await loginVM.onLoginWithAppleClick();
    };

    const onGoogleSignup = useGoogleLogin({
      onSuccess: signupVM.onSignupWithGoogleSuccess,
      onError: signupVM.onSignupWithGoogleError,
      flow: 'auth-code',
    });

    const onGoogleLogin = useGoogleLogin({
      onSuccess: loginVM.onLoginWithGoogleSuccess,
      onError: loginVM.onLoginWithGoogleError,
      flow: 'auth-code',
    });

    return (
      <ThemeProvider theme={themeState.current}>
        <ScreenContainer>
          <Container>
            <Content>
              {/*
               * preload the SVG gradient from the Google Play logo. if there are multiple
               * SVGs with the same linearGradient `id` on the same page, hiding the first one
               * using `display: none` makes the gradient disappear from all of them.
               * something like https://github.com/elderapo/react-svg-unique-id/ would be a
               * better fix, but the library is outdated and doesn't work within our environment.
               */}
              <DownloadGooglePlay style={{ width: 0, height: 0, position: 'absolute' }} />
              <LeftContainer style={{ flexDirection: 'column', alignItems: 'center' }}>
                <LeftContent>
                  {isUserAlreadyLoggedIn ? (
                    <View
                      style={{
                        width: '100%',
                        maxWidth: 400,
                      }}
                    >
                      <View
                        style={{
                          flex: 1,
                        }}
                      >
                        <Spacer />
                        <View
                          style={{ textAlign: 'center', alignItems: 'center', marginBottom: 16 }}
                        >
                          <ProfileImage diameter={100} source={coachProfilePic} />
                          <H3>{coachName ?? coachUsername}</H3>
                          <P2>
                            {acceptInviteVM.showDeclineButton
                              ? t('web.coach.acceptInvite.acceptOrDecline', {
                                  coach: coachName ?? coachUsername,
                                })
                              : t('web.coach.acceptInvite.wouldYouLikeToAccept', {
                                  coach: coachName ?? coachUsername,
                                })}
                          </P2>
                        </View>
                        <PrimaryButton
                          style={{ marginBottom: 16 }}
                          title={t('web.coach.acceptInvite.acceptInvite')}
                          onClick={() => {
                            toast.promise(acceptInviteVM.acceptInvite(), {
                              loading: t('web.coach.acceptInvite.acceptInvite.loading'),
                              success: t('web.coach.acceptInvite.acceptInvite.success'),
                              error: t('web.coach.acceptInvite.acceptInvite.error'),
                            });
                          }}
                          loading={acceptInviteVM.isAcceptingInvite}
                        />
                        {acceptInviteVM.showDeclineButton && (
                          <SecondaryButton
                            title={t('web.coach.acceptInvite.declineInvite')}
                            onClick={acceptInviteVM.declineInvite}
                            loading={acceptInviteVM.isDecliningInvite}
                          />
                        )}
                      </View>
                    </View>
                  ) : (
                    <>
                      <MobileContent>
                        <CoachCard>
                          <View style={{ height: 64, position: 'relative', alignItems: 'center' }}>
                            <ProfileImage
                              source={coachProfilePic}
                              diameter={96}
                              style={{
                                position: 'absolute',
                                top: -48,
                                borderColor: colors.white,
                                borderWidth: 4,
                                borderStyle: 'solid',
                                borderRadius: 104,
                              }}
                            />
                          </View>
                          <H3>{coachName ?? coachUsername}</H3>
                          <P2 style={{ marginTop: 8 }}>
                            {t('web.coach.acceptInvite.youCoarchTitle')}
                          </P2>
                        </CoachCard>
                      </MobileContent>
                      {acceptInviteVM.viewState === 'signup' && (
                        <SignupCard
                          email={signupVM.email}
                          onUpdateEmail={signupVM.onUpdateEmail}
                          emailErrorStatus={signupVM.emailErrorStatus}
                          password={signupVM.password}
                          onUpdatePassword={signupVM.onUpdatePassword}
                          passwordErrorStatus={signupVM.passwordErrorStatus}
                          isSignupEnabled={signupVM.isSignupButtonEnabled}
                          isLoading={signupVM.isLoading}
                          onSignup={onSignup}
                          signupErrorMessage={signupVM.errorMessage}
                          onGoogleSignup={onGoogleSignup}
                          onAppleSignup={onAppleSignupPress}
                          onExistingUserPress={() => {
                            acceptInviteVM.viewState = 'login';
                          }}
                        />
                      )}
                      {acceptInviteVM.viewState === 'login' && (
                        <>
                          {loginVM.screenState === 'login' && (
                            <LoginCard
                              usernameOrEmail={loginVM.usernameOrEmail}
                              onUpdateUsernameOrEmail={loginVM.onUpdateUsernameOrEmail}
                              password={loginVM.password}
                              onUpdatePassword={loginVM.onUpdatePassword}
                              isLoginEnabled={loginVM.isLoginButtonEnabled}
                              isLoading={loginVM.isLoading}
                              onLogin={onLogin}
                              loginErrorMessage={loginVM.errorMessage}
                              onGoogleLogin={onGoogleLogin}
                              onAppleLogin={onApplePress}
                              onForgotPassword={() => loginVM.setScreenState('forgot-password')}
                              onNewUserPress={() => {
                                acceptInviteVM.viewState = 'signup';
                              }}
                            />
                          )}
                          {loginVM.screenState === 'forgot-password' && (
                            <ForgotPasswordCard
                              isEnabled={loginVM.isSendPasswordRecoveryEnabled}
                              isLoading={loginVM.isPasswordRecoveryEmailLoading}
                              onUpdateEmail={loginVM.onUpdatePasswordRecoveryEmail}
                              onSendPasswordRecovery={loginVM.onSendPasswordRecovery}
                              onBackToLogin={() => loginVM.setScreenState('login')}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                </LeftContent>
              </LeftContainer>
              {!isUserAlreadyLoggedIn && (
                <RightContainer>
                  <CoachCard>
                    <View style={{ height: 64, position: 'relative', alignItems: 'center' }}>
                      <ProfileImage
                        source={coachProfilePic}
                        diameter={96}
                        style={{
                          position: 'absolute',
                          top: -48,
                          borderColor: colors.white,
                          borderWidth: 4,
                          borderStyle: 'solid',
                          borderRadius: 104,
                        }}
                      />
                    </View>
                    <H3>{coachName ?? coachUsername}</H3>
                    <P4 style={{ marginTop: 4, marginBottom: 12 }}>
                      {t('web.coach.acceptInvite.youCoarchTitle')}
                    </P4>
                    <P2>{t('web.coach.acceptInvite.welcomeAndAcceptMyInvite')}</P2>
                  </CoachCard>
                </RightContainer>
              )}
            </Content>
          </Container>
        </ScreenContainer>
        <Footer showPricingLink={false} />
      </ThemeProvider>
    );
  },
);

const Container = styled.div`
  width: 100vw;
  min-height: calc(100vh - 117px);
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  padding: 0;
  @media (max-width: 1024px) {
    padding: 0 40px;
  }
`;

const LeftContainer = styled.div`
  display: flex;
  flex: 1;
  width: 50%;
  align-items: center;
  justify-content: flex-start;
`;

const LeftContent = styled.div`
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  @media (max-width: 1024px) {
    padding: 40px 0;
  }
`;

const RightContainer = styled.div`
  display: flex;
  width: 50%;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  background-color: ${colors.greyUltraLight};
  @media (max-width: 1024px) {
    display: none;
  }
`;

const CoachCard = styled(Card)`
  width: 400px;
  padding: 24px;
  box-sizing: border-box;
  text-align: center;
`;

const MobileContent = styled.div`
  width: 100vw;
  align-items: center;
  justify-content: center;
  background-color: ${colors.greyUltraLight};
  display: none;
  margin: -40px;
  margin-bottom: 24px;
  padding: 48px 0 24px 0;
  @media (max-width: 1024px) {
    display: flex;
  }
`;
